import React from 'react'

import MoneyField from 'src/components/MoneyField'
import tipOptions from 'src/utils/tipOptions'

import CheckBox from './CheckBox'
import InfoPopover from './InfoPopover'
import SandwichField from './SandwichField'

// Tipping + cover-costs controls. Each section is independently gated:
// tipping needs the partner's tippingConfig; cover-costs needs a
// non-zero fee to cover (FTF partners + zero-fee payment methods land
// on fees == 0 and so don't render the row). When neither has anything
// to show, the whole component renders nothing — callers don't need to
// gate the mount themselves.
//
// The donation amount and total roll-up are *not* rendered here. AP/GP
// surface them in the wallet sheet, Stripe surfaces the total in the
// submit button text via donate_total_recurrence, and PayPal/Venmo on
// no-tipping no-cover-costs partners drops in DonationLine instead of
// this component (their SDK button doesn't reveal the amount until the
// sheet opens).

const Tipping = ({
  i18n, donation, partnerItem,
  tippingEnabled = false, coverCostsEnabled = false,
  // FTF flag from the goal_partner_item — drives the tip-section
  // body copy (FTF variant explains "Pledge already paid the card
  // fees", vanilla variant just asks for support). False is the safe
  // default: vanilla copy reads correctly even when the partner does
  // cover fees, while the FTF copy would mislead if the partner
  // doesn't.
  pledgeCoversFees = false,
}) => {
  const {
    state, dispatch, currency, fees, tippingConfig, toggleOtherTip,
  } = donation
  const beneficiaryName = partnerItem.beneficiary.name
  const isMultiBeneficiary = partnerItem.beneficiary.id == '_multiple'
  const coverCostsOrgName = isMultiBeneficiary
    ? i18n.t('selected_nonprofits')
    : beneficiaryName

  // Zero-tip "guilt trip" alert. Mirrors v6: only renders when the donor
  // has entered a non-zero donation but a zero tip (typically via Other
  // → 0). Organization beneficiaries get the _nonprofit variant — same
  // message register as the rest of the tip copy. The translation may
  // be empty in some locales, in which case we don't render at all.
  const isNonprofit = partnerItem.beneficiary.type == 'Organization'
  const tipZeroKey = 'tip_zero' + (isNonprofit ? '_nonprofit' : '')
  const tipZeroCopy = i18n.t(tipZeroKey)
  const showTipZero =
    tippingEnabled
    && !donation.bigAmount.eq(0)
    && donation.tip == 0
    && tipZeroCopy

  // Tip-section body copy. Picks between FTF ("Pledge is already
  // covering the card fees") and vanilla ("support our mission")
  // variants based on the goal partner's FTF setting, with a
  // _nonprofit suffix when the active beneficiary is an Organization
  // — the nonprofit variants reframe the ask around "100% of your
  // gift reaches the charity" instead of generic mission support.
  // Mirrors v6's resolveKey/resolveTranslation pattern in
  // donate_v6/fiat/Tipping.jsx.
  const tipCopyKey = (pledgeCoversFees ? 'tip_copy_ftf' : 'tip_copy_vanilla')
    + (isNonprofit ? '_nonprofit' : '')
  const tipCopy = i18n.t(tipCopyKey)

  // Inline disclaimer popover only when Pledge is actually covering
  // fees AND the locale supplies the disclaimer copy (some locales
  // leave it out — empty translation → no popover, no orphan icon).
  // Per-key tooltip wins when defined (the new tip_copy_*_tooltip
  // family lives next to the corresponding tip_copy_* in widgets.yml
  // so the disclaimer can shift wording when the headline shortens —
  // e.g. tip_copy_ftf_nonprofit_tooltip: "Pledge pays the card fees on
  // donations up to $1,000."). Falls back to the generic
  // pledge_covers_fees.disclaimer when no per-key override exists.
  const ftfDisclaimer = pledgeCoversFees && (
    i18n.t(`${tipCopyKey}_tooltip`) || i18n.t('pledge_covers_fees.disclaimer')
  )

  const showTipping = tippingEnabled && tippingConfig
  // Only render cover-costs when there's something to cover — defensive, like
  // the zero guards in utils/calcFees.js and completedDonation.js, not a live
  // case. <Tipping> mounts only on the three payment steps, and every method
  // that reaches them has a real branch in App.jsx's feeConfig switch, each
  // bundle gated on the same @payment_methods entry that offers the method at
  // all — so it can't be offered with its config missing. Apple/Google Pay carry
  // a second gate: discovery only runs when the card bundle exists, so those
  // rows can't appear without it. No untyped-amount case either — Start.jsx
  // won't advance until the amount validates against minAmount. The guard stays
  // because this component can't confirm its caller resolved a config.
  //
  // NOT FTF. `pledgeCoversFees` only picks tip copy and the disclaimer below —
  // it never touches feeConfig, so an FTF partner with cover-costs enabled gets
  // a full quote and sees this row. (Two earlier versions of this comment
  // claimed otherwise, each in a different way.)
  //
  // `fees` is a decimal string from a real config and a bare 0 with no config,
  // so `> 0` coerces on the string path. Intentional — but don't switch it to a
  // truthiness check, which would render the row for a '0.00' fee.
  const showCoverCosts = coverCostsEnabled && fees > 0

  // Nothing to render — let the caller drop the bordered box entirely
  // rather than ship a hairline divider with no content.
  if (!showTipping && !showCoverCosts) return null

  // Percentage / fixed-amount / zero / "Other" label rules all live in
  // utils/tipOptions, shared with v6's TippingSelect. The zero takes its
  // unit from the row rather than having a label of its own.
  const options = tipOptions({
    options: tippingConfig?.options,
    zeroTip: donation.zeroTip,
    currency,
    i18n,
  })

  // Empty value = "Other"; reducer's setTipOption clears otherTip on its
  // own when a real option is picked, so transitioning out of Other is
  // automatic.
  const handleTipChange = e => {
    if (e.target.value)
      dispatch({ type: 'setTipOption', value: e.target.value })
    else
      toggleOtherTip()
  }

  // Inline currency prefix/suffix for the Other-tip input — same logic
  // as Amount.jsx, kept local because nothing else in this component
  // needs it.
  const tipFieldFormat = (i18n.currency?.format || '%u%n').replace('%c', '').trim()
  const [ tipPrefixTpl = '', tipSuffixTpl = '' ] = tipFieldFormat.split('%n')
  const tipPrefixText = tipPrefixTpl.replace('%u', currency.symbol || '')
  const tipSuffixText = tipSuffixTpl.replace('%u', currency.symbol || '')

  return (
    <div className="rounded-lg border border-brand-500 bg-brand-50 divide-y divide-brand-500/30">
      {showTipping && (
        <div className="p-3 @roomy:p-4">
          {tipCopy && (
            <div className="text-sm text-gray-700 mb-2">
              {tipCopy}
              {ftfDisclaimer && (
                <>
                  {' '}
                  <InfoPopover
                    label={i18n.t('learn_more')}
                    boundary=".widget-content"
                  >
                    {ftfDisclaimer}
                  </InfoPopover>
                </>
              )}
            </div>
          )}
          <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
            <label
              htmlFor="tip-option"
              className="block font-medium cursor-pointer"
            >
              {i18n.t('subscription_labels.one_time.tipToPledge')}
            </label>
            <div className="-mt-1">
              <div className="flex gap-2">
                <select
                  id="tip-option"
                  className="form-select form-select-sm w-auto field-sizing-content"
                  value={state.otherTip ? '' : state.tipOption}
                  onChange={handleTipChange}
                >
                  {options.map(opt =>
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  )}
                </select>
                {state.otherTip && (
                  <SandwichField
                    type={MoneyField}
                    autoFocus
                    exponent={currency.exponent}
                    decimal={i18n.currency?.separator}
                    value={state.tipText}
                    onChange={e => dispatch({ type: 'setTipText', value: e.target.value })}
                    aria-label={i18n.t('other_tip_label')}
                    aria-invalid={donation.tipError ? 'true' : undefined}
                    placeholder={i18n.t('other_amount')}
                    className="form-control form-control-sm"
                    prefix={tipPrefixText && <span className="text-gray-500"> {tipPrefixText} </span>}
                    suffix={tipSuffixText && <span className="text-gray-500"> {tipSuffixText} </span>}
                  />
                )}
              </div>
              {donation.tipError && (
                <p className="text-sm text-danger max-w-max ms-auto mt-1">
                  {donation.tipError}
                </p>
              )}
            </div>
          </div>
          {showTipZero && (
            <div
              role="alert"
              className="mt-2 rounded border border-warning-300 bg-warning-50 text-warning-800 p-3 text-sm"
            >
              {tipZeroCopy}
            </div>
          )}
        </div>
      )}

      {showCoverCosts && (
        <div className="p-3 @roomy:p-4">
          <CheckBox
            id="cover-costs"
            checked={state.coverCosts}
            onChange={e => dispatch({ type: 'setCoverCosts', value: e.target.checked })}
            label={
              <span className="font-medium">
                {i18n.t('cover_costs_heading')} ({i18n.formatCurrency(fees, currency)})
              </span>
            }
            hint={i18n.t('cover_costs_selected', { org: coverCostsOrgName })}
            afterLabel={
              <InfoPopover
                label={i18n.t('learn_more')}
                boundary=".widget-content"
              >
                {i18n.t('cover_costs_tooltip')}
              </InfoPopover>
            }
          />
        </div>
      )}
    </div>
  )
}

export default Tipping
